import { useEffect, useRef } from 'react'
import { useSettings } from '@/context/settings-hooks'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'
import { isTextScale, type TextScale } from './text-scale'

/**
 * Two-way sync of the text size with the user's profile (`textScale`), so the
 * choice follows the user across devices. localStorage stays the fast local
 * cache in SettingsContext; this layers the server on top, exactly as
 * useThemeServerSync does for the theme.
 *
 * - On first profile load: apply the saved size (server wins on login).
 * - On later local changes: save the new size (deduped).
 * - A profile with no saved size triggers NO write on login.
 *
 * Mount once inside the authenticated tree (see AppLayout).
 */
export function useTextScaleServerSync(): void {
  const { textScale, setTextScale } = useSettings()
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const initialized = useRef(false)
  const lastSynced = useRef<TextScale | null>(null)

  useEffect(() => {
    if (!user) return

    if (!initialized.current) {
      initialized.current = true
      const saved = isTextScale(user.textScale) ? user.textScale : null
      lastSynced.current = saved ?? textScale
      // Return BEFORE the push path: setTextScale re-runs this effect with the
      // applied size, and this commit's `textScale` is still stale.
      if (saved && saved !== textScale) setTextScale(saved)
      return
    }

    if (textScale !== lastSynced.current) {
      lastSynced.current = textScale
      void updateUser({ textScale })
    }
  }, [user, textScale, setTextScale, updateUser])
}
